//import { skillTrainerData } from "./skillTrainerData";
//const rates = skillTrainerData.getRates();
//const pracGains = skillTrainerData.getPracGainsByRace();
//const pracCosts = skillTrainerData.getPracCostsByProfession();

class SkillTrainerCalc2 {
  getTrainerResults(state) {
    const player = {
      professionId: state.professionId,
      strength:     state.strn,
      intelligence: state.intl,
      willpower:    state.wilp,
      dexterity:    state.dext,
      constitution: state.cont,
    };

    const targets = state.skills
      //.filter(sk => sk.percentTarget > 0)
      .map(sk => ({ skillId: sk.id, targetPercent: sk.percentTarget ?? 0 }));

    // Build startPercents from state so the solver knows where each skill begins
    const startPercents = {};
    for (const sk of state.skills) {
      startPercents[sk.id] = sk.percentStart ?? 0;
    }

    const result = findOptimalPracticeSequence(player, targets, startPercents);

    return transformToLegacyResult(player, targets, result, state);
  }
}

export const skillTrainerCalc2 = new SkillTrainerCalc2();

// ─── IndexedMinHeap ──────────────────────────────────────────────────────────

class IndexedMinHeap {
  constructor() {
    this._data = [];
    this._pos  = new Map();
  }

  get size()  { return this._data.length; }
  isEmpty()   { return this._data.length === 0; }

  push(item) {
    const i = this._data.length;
    this._data.push(item);
    this._pos.set(item.key, i);
    this._up(i);
  }

  pop() {
    const top  = this._data[0];
    const last = this._data.pop();
    this._pos.delete(top.key);
    if (this._data.length > 0) {
      this._data[0] = last;
      this._pos.set(last.key, 0);
      this._down(0);
    }
    return top;
  }

  upsert(item) {
    const i = this._pos.get(item.key);
    if (i === undefined) {
      this.push(item);
    } else {
      this._data[i] = item;
      this._up(i);
    }
  }

  _swap(i, j) {
    this._pos.set(this._data[i].key, j);
    this._pos.set(this._data[j].key, i);
    [this._data[i], this._data[j]] = [this._data[j], this._data[i]];
  }

  _up(i) {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this._compare(this._data[parent], this._data[i]) <= 0) break;
      this._swap(parent, i);
      i = parent;
    }
  }

  _down(i) {
    const n = this._data.length;
    for (;;) {
      let smallest = i;
      const left   = 2 * i + 1;
      const right  = 2 * i + 2;
      if (left  < n && this._compare(this._data[left],  this._data[smallest]) < 0) smallest = left;
      if (right < n && this._compare(this._data[right], this._data[smallest]) < 0) smallest = right;
      if (smallest === i) break;
      this._swap(smallest, i);
      i = smallest;
    }
  }

  _compare(a, b) {
    if (a.cost         !== b.cost)         return a.cost         - b.cost;
    if (a.totalPct     !== b.totalPct)     return b.totalPct     - a.totalPct;
    if (a.profSwitches !== b.profSwitches) return a.profSwitches - b.profSwitches;
    return a.switches - b.switches;
  }
}

// ─── Game mechanics ──────────────────────────────────────────────────────────

function nextPercent(player, skill) {
  let rateKey;
  switch (skill.professionId) {
    case 'warrior':
      rateKey = Math.floor(player.strength / 2) +
                Math.floor(player.dexterity / 4) +
                Math.floor(player.constitution / 4);
      break;
    case 'rogue':
      rateKey = Math.floor(player.dexterity * 3 / 4) + Math.floor(player.intelligence / 4);
      break;
    case 'hunter':
      rateKey = Math.floor((player.strength + player.intelligence +
                            player.willpower + player.dexterity) / 4);
      break;
    case 'fade':
      rateKey = player.intelligence;
      break;
    default:
      throw new Error('invalid profession: ' + skill.professionId);
  }

  let percent = skill.percent;
  if      (percent <= 20) percent += Math.floor(skill.weight * data.rates[rateKey][0]);
  else if (percent <= 40) percent += Math.floor(skill.weight * data.rates[rateKey][1]);
  else if (percent <= 60) percent += Math.floor(skill.weight * data.rates[rateKey][2]);
  else if (percent <= 80) percent += Math.floor(skill.weight * data.rates[rateKey][3]);
  else if (percent <= 90) percent += Math.floor(skill.weight * data.rates[rateKey][4]);
  else                    percent += 1;
  return percent;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function pracCost(player, skill) {
  return data.professions.find(p => p.id === player.professionId)
                         .pracCost[skill.professionId];
}

function computeAllCeilings(skills, targetMap, player) {
  const profCosts = data.professions.find(p => p.id === player.professionId).pracCost;

  const ceilingMap = {};
  for (const sk of skills) ceilingMap[sk.id] = targetMap[sk.id] ?? 0;

  let changed = true;
  while (changed) {
    changed = false;
    for (const sk of skills) {
      const skCost = profCosts[sk.professionId];
      for (const rid of sk.residualIds) {
        const rCeil = ceilingMap[rid] ?? 0;
        if (rCeil === 0) continue;
        const rSk = skills.find(s => s.id === rid);
        if (!rSk) continue;
        const rCost = profCosts[rSk.professionId];
        if (skCost >= rCost) continue;
        const needed = Math.min(Math.ceil(rCeil / 0.4), 99);
        if (needed > ceilingMap[sk.id]) { ceilingMap[sk.id] = needed; changed = true; }
      }
    }
  }

  for (const id of Object.keys(ceilingMap)) ceilingMap[id] = Math.min(ceilingMap[id], 99);
  return ceilingMap;
}

function connectedSkills(targetIds) {
  const visited = new Set(targetIds);
  const queue   = [...targetIds];
  while (queue.length) {
    const id = queue.shift();
    const sk = data.skills.find(s => s.id === id);
    if (!sk) continue;
    for (const rid of sk.residualIds) {
      if (!visited.has(rid)) { visited.add(rid); queue.push(rid); }
    }
    for (const s of data.skills) {
      if (!visited.has(s.id) && s.residualIds.includes(id)) {
        visited.add(s.id); queue.push(s.id);
      }
    }
  }
  return [...visited].map(id => data.skills.find(s => s.id === id));
}

function decomposeTargets(targets) {
  const components = [];
  const assigned   = new Set();
  for (const t of targets) {
    if (assigned.has(t.skillId)) continue;
    const connected   = connectedSkills([t.skillId]).map(s => s.id);
    const compTargets = targets.filter(tt => connected.includes(tt.skillId));
    compTargets.forEach(tt => assigned.add(tt.skillId));
    components.push(compTargets);
  }
  return components;
}

// ─── Core Dijkstra solver for one connected component ───────────────────────

function solveComponent(player, targets, startPercents, MAX_STATES = 500_000) {
  targets = targets.filter(t => t.targetPercent > 0);
  if (targets.length === 0) {
    return { totalCost: 0, sequence: [], finalPercents: {}, statesExplored: 0 };
  }

  const targetMap = {};
  for (const t of targets) targetMap[t.skillId] = t.targetPercent;

  const allConnected = connectedSkills(Object.keys(targetMap));
  const ceilings     = computeAllCeilings(allConnected, targetMap, player);
  const tracked      = allConnected.filter(sk => ceilings[sk.id] > 0);

  if (tracked.length === 0) {
    return { totalCost: 0, sequence: [], finalPercents: {}, statesExplored: 0 };
  }

  const caps = tracked.map(sk => ceilings[sk.id] + 1);

  const idxOf    = new Map(tracked.map((sk, i) => [sk.id, i]));
  const costOf   = new Map(tracked.map(sk => [sk.id, pracCost(player, sk)]));
  const resIdxOf = tracked.map(sk =>
    sk.residualIds.map(rid => idxOf.get(rid)).filter(i => i !== undefined)
  );

  function clamp(percents) {
    return percents.map((v, i) => Math.min(v, caps[i]));
  }

  function makeKey(percents, lastSkillId) {
    return percents.join(',') + '|' + (lastSkillId ?? '');
  }

  function isGoal(percents) {
    return targets.every(t => percents[idxOf.get(t.skillId)] >= t.targetPercent);
  }

  function applyPracticeLocal(percents, skillIdx) {
    const np = percents.slice();
    const sk = tracked[skillIdx];
    np[skillIdx] = nextPercent(player, Object.assign({}, sk, { percent: np[skillIdx] }));
    for (const ri of resIdxOf[skillIdx]) {
      np[ri] = Math.max(np[ri], Math.floor(np[skillIdx] * 0.4));
    }
    return np;
  }

  // Build initial percents from startPercents, then apply residuals from those
  // starting values so the solver begins in the correct state.
  const rawInit = tracked.map(sk => startPercents[sk.id] ?? 0);
  // Propagate starting residuals: if skill A starts at X, skill B (a residual
  // of A) should start at at least floor(X * 0.4), matching old code behavior.
  for (let i = 0; i < tracked.length; i++) {
    if (rawInit[i] > 0) {
      for (const ri of resIdxOf[i]) {
        rawInit[ri] = Math.max(rawInit[ri], Math.floor(rawInit[i] * 0.4));
      }
    }
  }
  const initPercents = clamp(rawInit);

  if (isGoal(initPercents)) {
    return {
      totalCost: 0, sequence: [],
      finalPercents: Object.fromEntries(tracked.map((sk, i) => [sk.id, initPercents[i]])),
      statesExplored: 0,
    };
  }

  const initKey = makeKey(initPercents, null);

  const dist   = new Map([[initKey, { cost: 0, totalPct: 0, profSwitches: 0, switches: 0 }]]);
  const parent = new Map([[initKey, null]]);

  const heap = new IndexedMinHeap();
  heap.push({
    key: initKey,
    cost: 0, totalPct: 0, switches: 0, profSwitches: 0,
    lastSkillId: null, lastProfessionId: null,
    percents: initPercents,
  });

  let statesExplored = 0;

  while (!heap.isEmpty()) {
    if (statesExplored >= MAX_STATES) {
      console.warn(`[solveComponent] MAX_STATES (${MAX_STATES}) hit. Partial result.`);
      break;
    }

    const { key, cost, totalPct, switches, profSwitches,
            lastSkillId, lastProfessionId, percents } = heap.pop();
    statesExplored++;

    const best = dist.get(key);
    if (best) {
      if (cost > best.cost) continue;
      if (cost === best.cost) {
        if (totalPct    <  best.totalPct)                                      continue;
        if (totalPct   === best.totalPct && profSwitches >  best.profSwitches) continue;
        if (totalPct   === best.totalPct && profSwitches === best.profSwitches
                                         && switches    >  best.switches)      continue;
      }
    }

    if (isGoal(percents)) {
      const sequence = [];
      let k = key;
      while (parent.get(k) !== null) {
        const p = parent.get(k);
        sequence.push(p.skillId);
        k = p.parentKey;
      }
      sequence.reverse();

      return {
        totalCost: cost, sequence,
        finalPercents: Object.fromEntries(tracked.map((sk, i) => [sk.id, percents[i]])),
        statesExplored,
      };
    }

    for (const sk of tracked) {
      const i = idxOf.get(sk.id);
      if (percents[i] >= ceilings[sk.id]) continue;

      const newPercents     = clamp(applyPracticeLocal(percents, i));
      const newCost         = cost        + costOf.get(sk.id);
      const newTotalPct     = newPercents.reduce((s, p) => s + p, 0);
      const newProfSwitches = profSwitches + (lastProfessionId === null || sk.professionId === lastProfessionId ? 0 : 1);
      const newSwitches     = switches     + (lastSkillId      === null || sk.id            === lastSkillId     ? 0 : 1);
      const newKey          = makeKey(newPercents, sk.id);

      const ex = dist.get(newKey);
      const better =
        !ex                                            ? true  :
        newCost         < ex.cost                     ? true  :
        newCost         > ex.cost                     ? false :
        newTotalPct     > ex.totalPct                 ? true  :
        newTotalPct     < ex.totalPct                 ? false :
        newProfSwitches < ex.profSwitches              ? true  :
        newProfSwitches > ex.profSwitches              ? false :
        newSwitches     < ex.switches;

      if (better) {
        dist.set(newKey,   { cost: newCost, totalPct: newTotalPct,
                             profSwitches: newProfSwitches, switches: newSwitches });
        parent.set(newKey, { parentKey: key, skillId: sk.id });
        heap.upsert({
          key: newKey,
          cost: newCost, totalPct: newTotalPct,
          switches: newSwitches, profSwitches: newProfSwitches,
          lastSkillId: sk.id, lastProfessionId: sk.professionId,
          percents: newPercents,
        });
      }
    }
  }

  return { totalCost: Infinity, sequence: [], finalPercents: {}, statesExplored };
}

// ─── Main solver ─────────────────────────────────────────────────────────────

function findOptimalPracticeSequence(player, targets, startPercents = {}, maxStatesPerComponent = 500_000) {
  const components = decomposeTargets(targets);

  let totalCost          = 0;
  let combinedSequence   = [];
  const combinedPercents = {};
  let totalStates        = 0;
  const componentResults = [];

  for (const compTargets of components) {
    const result = solveComponent(player, compTargets, startPercents, maxStatesPerComponent);
    totalCost         += result.totalCost;
    combinedSequence   = combinedSequence.concat(result.sequence);
    Object.assign(combinedPercents, result.finalPercents);
    totalStates       += result.statesExplored;
    componentResults.push({ targets: compTargets, ...result });
  }

  return {
    totalCost,
    sequence: combinedSequence,
    finalPercents: combinedPercents,
    statesExplored: totalStates,
    components: componentResults,
  };
}

// ─── Legacy shape transformer ─────────────────────────────────────────────────

function transformToLegacyResult(player, targets, result, state) {
  // Build skillsMap seeded with percentStart from state, then propagate
  // starting residuals before replaying the sequence.
  const skillsMap = {};
  for (const sk of data.skills) {
    const stateSkill = state.skills.find(s => s.id === sk.id);
    skillsMap[sk.id] = {
      ...sk,
      percentActual:  stateSkill?.percentStart ?? 0,
      percentStart:   stateSkill?.percentStart ?? 0,
      percentTarget:  targets.find(t => t.skillId === sk.id)?.targetPercent ?? 0,
      pracsActual:    0,
      pracsCount:     0,
      percentHistory: [],
    };
  }

  // Propagate starting residuals before replaying the sequence,
  // matching the old code's initialize-residuals step.
  for (const sk of Object.values(skillsMap)) {
    if (sk.percentActual > 0) {
      const residual = Math.floor(sk.percentActual * 0.4);
      for (const rid of sk.residualIds) {
        if (skillsMap[rid]) {
          skillsMap[rid].percentActual = Math.max(skillsMap[rid].percentActual, residual);
        }
      }
    }
  }

  const profCosts = data.professions.find(p => p.id === player.professionId).pracCost;

  // Replay the Dijkstra sequence to reconstruct per-skill history
  for (const skillId of result.sequence) {
    const sk = skillsMap[skillId];
    sk.percentActual = nextPercent(player, { ...sk, percent: sk.percentActual });
    sk.pracsActual  += profCosts[sk.professionId];
    sk.pracsCount++;
    sk.percentHistory.push(sk.percentActual);

    for (const rid of sk.residualIds) {
      if (skillsMap[rid]) {
        skillsMap[rid].percentActual = Math.max(
          skillsMap[rid].percentActual,
          Math.floor(sk.percentActual * 0.4)
        );
      }
    }
  }

  // Only include skills that were targeted or appeared in the sequence
  const touchedIds = new Set([
    ...targets.map(t => t.skillId),
    ...result.sequence,
  ]);
  const skills = [...touchedIds].map(id => skillsMap[id]).filter(Boolean);

  // Build commands — PR strings grouped by profession
  const commands = { warrior: '', rogue: '', hunter: '', fade: '' };
  for (const sk of skills) {
    commands[sk.professionId] += `PR ${sk.name}\n`.repeat(sk.pracsCount);
  }
  for (const key of Object.keys(commands)) {
    commands[key] = commands[key].slice(0, -1);
  }

  // ── Residual order ────────────────────────────────────────────────────────

  const grouped = {};
  for (const sk of skills) {
    if (!sk.residualGroup) continue;
    if (!sk.percentTarget && sk.pracsCount === 0) continue;
    if (!grouped[sk.residualGroup]) grouped[sk.residualGroup] = [];
    grouped[sk.residualGroup].push(sk);
  }

  const crossGroups = Object.values(grouped).filter(group =>
    group.some(a => group.some(b => a.professionId !== b.professionId))
  );

  let residualOrderIds  = [];
  let residualOrderType = '';

  if (crossGroups.length) {
    const crossIdSets = crossGroups.map(group => {
      const groupSkillIds = new Set(group.map(s => s.id));
      const seen = [];
      for (const skillId of result.sequence) {
        if (!groupSkillIds.has(skillId)) continue;
        const prof = skillsMap[skillId].professionId;
        if (!seen.length || seen[seen.length - 1] !== prof) seen.push(prof);
      }
      return seen;
    });

    const activeCrossGroups = crossGroups.filter((_, i) => new Set(crossIdSets[i]).size > 1);
    const activeCrossIdSets = crossIdSets.filter(ids => new Set(ids).size > 1);

    if (activeCrossGroups.length) {
      const seen = [];
      for (const ids of activeCrossIdSets) {
        for (const id of ids) {
          if (!seen.includes(id)) seen.push(id);
        }
      }
      const professionIds = seen;

      const professionOrderWorks = activeCrossIdSets.every(crossIds => {
        const filtered = professionIds.filter(x => crossIds.includes(x));
        return filtered.every((v, i) => v === crossIds[i]);
      });

      if (professionOrderWorks) {
        residualOrderIds  = professionIds;
        residualOrderType = 'profession';
      } else {
        const crossSkillIds = new Set(activeCrossGroups.flatMap(g => g.map(s => s.id)));
        const seenSkills = [];
        for (const skillId of result.sequence) {
          if (crossSkillIds.has(skillId) && !seenSkills.includes(skillId)) {
            seenSkills.push(skillId);
          }
        }
        residualOrderIds  = seenSkills;
        residualOrderType = 'skill';
      }
    }
  }

  // ── Level calculation ─────────────────────────────────────────────────────
  const pracGain   = data.races.find(r => r.id === state.raceId)?.pracGain ?? 5;
  const pracs      = result.totalCost;

  let level      = 1;
  let levelPracs = state.bonded ? 18 : 8;
  while (levelPracs < pracs) {
    level++;
    levelPracs += level <= 30 ? pracGain : 2;
  }
  const remainderPracs = pracs ? levelPracs - pracs : 0;

  return {
    skills,
    commands,
    residualOrderIds,
    residualOrderType,
    pracs,
    remainderPracs,
    level,
  };
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const data = {
  "skills": [
    { "id": "bash",             "name": "Bash",             "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "",  "residualIds": [] },
    { "id": "kick",             "name": "Kick",             "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "",  "residualIds": [] },
    { "id": "rescue",           "name": "Rescue",           "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "",  "residualIds": [] },
    { "id": "shield_parry",     "name": "Shield Parry",     "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "",  "residualIds": [] },
    { "id": "charge",           "name": "Charge",           "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "",  "residualIds": [] },
    { "id": "long_blades",      "name": "Long Blades",      "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "1", "residualIds": ["medium_blades","fencing_blades"] },
    { "id": "medium_blades",    "name": "Medium Blades",    "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "1", "residualIds": ["long_blades","fencing_blades"] },
    { "id": "fencing_blades",   "name": "Fencing Blades",   "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "1", "residualIds": ["long_blades","medium_blades"] },
    { "id": "axes",             "name": "Axes",             "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "3", "residualIds": ["clubs","staves"] },
    { "id": "clubs",            "name": "Clubs",            "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "3", "residualIds": ["axes","staves"] },
    { "id": "staves",           "name": "Staves",           "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "3", "residualIds": ["axes","clubs"] },
    { "id": "lances",           "name": "Lances",           "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "4", "residualIds": ["spears","javelins","polearms"] },
    { "id": "spears",           "name": "Spears",           "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "4", "residualIds": ["lances","javelins","polearms"] },
    { "id": "javelins",         "name": "Javelins",         "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "4", "residualIds": ["lances","spears","polearms"] },
    { "id": "polearms",         "name": "Polearms",         "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "4", "residualIds": ["lances","spears","javelins"] },
    { "id": "flails",           "name": "Flails",           "defunct": false, "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "5", "residualIds": ["whips","chains"] },
    { "id": "whips",            "name": "Whips",            "defunct": true,  "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "5", "residualIds": ["flails","chains"] },
    { "id": "chains",           "name": "Chains",           "defunct": true,  "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "5", "residualIds": ["flails","whips"] },
    { "id": "bows",             "name": "Bows",             "defunct": true,  "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "2", "residualIds": ["crossbows","slings"] },
    { "id": "crossbows",        "name": "Crossbows",        "defunct": true,  "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "2", "residualIds": ["bows","slings"] },
    { "id": "slings",           "name": "Slings",           "defunct": true,  "useLevel": false, "weight": 1,   "professionId": "warrior", "residualGroup": "2", "residualIds": ["bows","crossbows"] },
    { "id": "steal",            "name": "Steal",            "defunct": false, "useLevel": false, "weight": 1.5, "professionId": "rogue",   "residualGroup": "",  "residualIds": [] },
    { "id": "pick",             "name": "Pick",             "defunct": false, "useLevel": false, "weight": 1,   "professionId": "rogue",   "residualGroup": "",  "residualIds": [] },
    { "id": "hide",             "name": "Hide",             "defunct": false, "useLevel": false, "weight": 1,   "professionId": "rogue",   "residualGroup": "6", "residualIds": ["camouflage"] },
    { "id": "sneak",            "name": "Sneak",            "defunct": false, "useLevel": false, "weight": 1,   "professionId": "rogue",   "residualGroup": "7", "residualIds": ["ranger_sneak"] },
    { "id": "dodge",            "name": "Dodge",            "defunct": false, "useLevel": false, "weight": 1,   "professionId": "rogue",   "residualGroup": "",  "residualIds": [] },
    { "id": "attack",           "name": "Attack",           "defunct": false, "useLevel": false, "weight": 1,   "professionId": "rogue",   "residualGroup": "",  "residualIds": [] },
    { "id": "backstab",         "name": "Backstab",         "defunct": false, "useLevel": false, "weight": 1,   "professionId": "rogue",   "residualGroup": "",  "residualIds": [] },
    { "id": "palm",             "name": "Palm",             "defunct": false, "useLevel": false, "weight": 1.5, "professionId": "rogue",   "residualGroup": "",  "residualIds": [] },
    { "id": "short_blades",     "name": "Short Blades",     "defunct": false, "useLevel": false, "weight": 1,   "professionId": "rogue",   "residualGroup": "1", "residualIds": ["long_blades","medium_blades","fencing_blades"] },
    { "id": "projectiles",      "name": "Projectiles",      "defunct": false, "useLevel": false, "weight": 1,   "professionId": "rogue",   "residualGroup": "2", "residualIds": ["bows","crossbows","slings"] },
    { "id": "search",           "name": "Search",           "defunct": false, "useLevel": false, "weight": 1,   "professionId": "hunter",  "residualGroup": "",  "residualIds": [] },
    { "id": "track",            "name": "Track",            "defunct": false, "useLevel": false, "weight": 1,   "professionId": "hunter",  "residualGroup": "",  "residualIds": [] },
    { "id": "ride",             "name": "Ride",             "defunct": false, "useLevel": true,  "weight": 1,   "professionId": "hunter",  "residualGroup": "",  "residualIds": [] },
    { "id": "wisdom_lore",      "name": "Wisdom Lore",      "defunct": false, "useLevel": true,  "weight": 1,   "professionId": "hunter",  "residualGroup": "",  "residualIds": [] },
    { "id": "swim",             "name": "Swim",             "defunct": false, "useLevel": false, "weight": 1.5, "professionId": "hunter",  "residualGroup": "",  "residualIds": [] },
    { "id": "notice",           "name": "Notice",           "defunct": false, "useLevel": false, "weight": 1,   "professionId": "hunter",  "residualGroup": "",  "residualIds": [] },
    { "id": "camouflage",       "name": "Camouflage",       "defunct": false, "useLevel": false, "weight": 1,   "professionId": "hunter",  "residualGroup": "6", "residualIds": ["hide"] },
    { "id": "ranger_sneak",     "name": "Ranger Sneak",     "defunct": false, "useLevel": false, "weight": 1,   "professionId": "hunter",  "residualGroup": "7", "residualIds": ["sneak"] },
    { "id": "cover_tracks",     "name": "Cover Tracks",     "defunct": false, "useLevel": false, "weight": 1.5, "professionId": "hunter",  "residualGroup": "",  "residualIds": [] },
    { "id": "survival",         "name": "Survival",         "defunct": false, "useLevel": true,  "weight": 1,   "professionId": "hunter",  "residualGroup": "",  "residualIds": [] },
    { "id": "sense",            "name": "Sense",            "defunct": false, "useLevel": false, "weight": 1,   "professionId": "fade",    "residualGroup": "",  "residualIds": [] },
    { "id": "fade",             "name": "Fade",             "defunct": false, "useLevel": false, "weight": 1,   "professionId": "fade",    "residualGroup": "",  "residualIds": [] },
    { "id": "compel",           "name": "Compel",           "defunct": false, "useLevel": false, "weight": 1,   "professionId": "fade",    "residualGroup": "",  "residualIds": [] },
    { "id": "darken",           "name": "Darken",           "defunct": false, "useLevel": false, "weight": 1,   "professionId": "fade",    "residualGroup": "",  "residualIds": [] },
    { "id": "fear",             "name": "Fear",             "defunct": false, "useLevel": false, "weight": 1,   "professionId": "fade",    "residualGroup": "",  "residualIds": [] },
    { "id": "sense_channeling", "name": "Sense Channeling", "defunct": false, "useLevel": false, "weight": 1,   "professionId": "fade",    "residualGroup": "",  "residualIds": [] }
  ],
  "races": [
    { "id": "human",     "name": "Human",     "pracGain": 5, "professionIds": ["warrior","rogue","hunter","mchanneler","fchanneler"] },
    { "id": "seanchan",  "name": "Seanchan",  "pracGain": 5, "professionIds": ["warrior","rogue","hunter","fchanneler"] },
    { "id": "trolloc",   "name": "Trolloc",   "pracGain": 3, "professionIds": ["warrior","rogue","hunter"] },
    { "id": "fade",      "name": "Fade",      "pracGain": 4, "professionIds": ["fade"] },
    { "id": "dreadlord", "name": "Dreadlord", "pracGain": 5, "professionIds": ["mchanneler","fchanneler"] },
    { "id": "grayman",   "name": "Grayman",   "pracGain": 5, "professionIds": ["warrior","rogue","hunter"] }
  ],
  "professions": [
    { "id": "warrior",    "name": "Warrior",     "pracCost": { "warrior": 1, "rogue": 3, "hunter": 2, "fade": 0 }, "raceIds": ["human","seanchan","trolloc","grayman"] },
    { "id": "rogue",      "name": "Rogue",        "pracCost": { "warrior": 3, "rogue": 1, "hunter": 2, "fade": 0 }, "raceIds": ["human","seanchan","trolloc","grayman"] },
    { "id": "hunter",     "name": "Hunter",       "pracCost": { "warrior": 2, "rogue": 2, "hunter": 1, "fade": 0 }, "raceIds": ["human","seanchan","trolloc","grayman"] },
    { "id": "fade",       "name": "Fade",         "pracCost": { "warrior": 1, "rogue": 1, "hunter": 1, "fade": 1 }, "raceIds": ["fade"] },
    { "id": "mchanneler", "name": "M. Channeler", "pracCost": { "warrior": 3, "rogue": 3, "hunter": 2, "fade": 0 }, "raceIds": ["human","dreadlord"] },
    { "id": "fchanneler", "name": "F. Channeler", "pracCost": { "warrior": 4, "rogue": 3, "hunter": 2, "fade": 0 }, "raceIds": ["human","seanchan","dreadlord"] }
  ],
  "rates": {
    "6":  [6,4,3,2,1],   "7":  [7,5,4,2,1],   "8":  [8,6,4,3,1],   "9":  [9,7,5,3,1],
    "10": [10,8,5,4,2],  "11": [11,8,6,4,2],  "12": [12,9,7,4,2],  "13": [13,10,7,5,2],
    "14": [14,11,8,5,2], "15": [15,12,8,6,3], "16": [16,12,9,6,3], "17": [17,13,10,6,3],
    "18": [18,14,10,7,3],"19": [19,15,11,7,3]
  }
};